import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../ThemeContext";
import "../landing.css";

const generateRoomCode = () => {
  const c = 'abcdefghijklmnopqrstuvwxyz';
  const s = (n) => Array.from({ length: n }, () => c[Math.floor(Math.random() * c.length)]).join('');
  return `${s(3)}-${s(4)}-${s(3)}`;
};

const Landing = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const [joinValue, setJoinValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const [scheduledLink, setScheduledLink] = useState("");
  const [scheduledCopied, setScheduledCopied] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleInstantStart = () => {
    navigate(`/meet/${generateRoomCode()}`);
  };

  const handleSchedule = () => {
    setShowDropdown(false);
    const now = new Date();
    setScheduleDate(now.toISOString().split("T")[0]);
    setScheduleTime(
      `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`
    );
    setScheduledLink("");
    setShowScheduleModal(true);
  };

  const handleGenerateScheduledLink = () => {
    const code = generateRoomCode();
    setScheduledLink(`${window.location.origin}/meet/${code}`);
  };

  const handleCopyScheduledLink = () => {
    navigator.clipboard.writeText(scheduledLink);
    setScheduledCopied(true);
    setTimeout(() => setScheduledCopied(false), 1800);
  };

  const handleJoin = () => {
    const val = joinValue.trim();
    if (!val) return alert("Enter a meeting ID or link to join.");
    // Accept full URL or just the code
    const code = val.includes("/meet/") ? val.split("/meet/")[1] : val;
    navigate(`/meet/${code}`);
  };

  return (
    <div className="landing-page">
      {/* ── Navbar ── */}
      <nav className="l-nav">
        <img src="https://aozo.in/sheild_logo.png" alt="AOZO" className="l-nav-logo" />
        <div className="l-nav-right">
          <button className="theme-toggle" onClick={toggleTheme} title="Toggle theme">
            <i className={`fas ${theme === "dark" ? "fa-sun" : "fa-moon"}`}></i>
          </button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <main className="l-hero">
        <div className="l-hero-left">
          <h1 className="l-hero-title">
            Video calls and meetings
            <br />
            <span className="l-hero-accent">for everyone</span>
          </h1>
          <p className="l-hero-sub">
            Connect, collaborate, and celebrate from anywhere with AOZO Meet.
            Free, secure, and instant — no downloads needed.
          </p>

          {/* CTA row */}
          <div className="l-cta-row">
            {/* New Meeting dropdown */}
            <div className="l-dropdown-wrap" ref={dropdownRef}>
              <button
                className="btn-new-meeting"
                onClick={() => setShowDropdown(s => !s)}
              >
                <i className="fas fa-video"></i>
                New Meeting
                <i className={`fas fa-chevron-${showDropdown ? "up" : "down"} l-chevron`}></i>
              </button>

              {showDropdown && (
                <div className="l-dropdown">
                  <button className="l-dropdown-item" onClick={handleInstantStart}>
                    <span className="l-dropdown-icon green-icon">
                      <i className="fas fa-bolt"></i>
                    </span>
                    <div>
                      <div className="l-dropdown-title">Instant Start</div>
                      <div className="l-dropdown-sub">Start a meeting right now</div>
                    </div>
                  </button>
                  <button className="l-dropdown-item" onClick={handleSchedule}>
                    <span className="l-dropdown-icon blue-icon">
                      <i className="fas fa-calendar-alt"></i>
                    </span>
                    <div>
                      <div className="l-dropdown-title">Schedule for Later</div>
                      <div className="l-dropdown-sub">Get a link to share ahead of time</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            {/* Join group */}
            <div className="l-join-group">
              <i className="fas fa-keyboard l-join-icon"></i>
              <input
                className="l-join-input"
                type="text"
                placeholder="Enter meeting ID or link"
                value={joinValue}
                onChange={(e) => setJoinValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleJoin()}
              />
              <button
                className="btn-join"
                onClick={handleJoin}
                disabled={!joinValue.trim()}
              >
                Join
              </button>
            </div>
          </div>

          <p className="l-hint">
            <i className="fas fa-lock"></i> Your meeting is safe — no one can join without the link
          </p>
        </div>

        <div className="l-hero-right">
          <div className="l-img-wrap">
            <img src="/video.jpg" alt="Video call preview" className="l-hero-img" />
            <div className="l-img-badge">
              <i className="fas fa-shield-alt"></i> End-to-end encrypted
            </div>
          </div>
        </div>
      </main>

      {/* ── Feature strip ── */}
      <section className="l-features">
        {[
          { icon: "fa-bolt", text: "Instant connect" },
          { icon: "fa-lock", text: "Encrypted" },
          { icon: "fa-users", text: "Group meetings" },
          { icon: "fa-mobile-alt", text: "Any device" },
        ].map(({ icon, text }) => (
          <div className="l-feature" key={text}>
            <i className={`fas ${icon}`}></i>
            <span>{text}</span>
          </div>
        ))}
      </section>

      <footer className="l-footer">
        &copy; {new Date().getFullYear()} AOZO Meet. All rights reserved.
      </footer>

      {/* ── Schedule Modal ── */}
      {showScheduleModal && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span><i className="fas fa-calendar-alt"></i> Schedule a Meeting</span>
              <button className="modal-close" onClick={() => setShowScheduleModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-field">
                <label>Date</label>
                <input
                  type="date"
                  value={scheduleDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setScheduleDate(e.target.value)}
                />
              </div>
              <div className="modal-field">
                <label>Time</label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                />
              </div>

              {!scheduledLink ? (
                <button
                  className="modal-btn-primary"
                  onClick={handleGenerateScheduledLink}
                  disabled={!scheduleDate || !scheduleTime}
                >
                  <i className="fas fa-link"></i> Generate Meeting Link
                </button>
              ) : (
                <div className="modal-link-result">
                  <p className="modal-link-label">
                    <i className="fas fa-check-circle" style={{ color: "var(--green)" }}></i>
                    &nbsp; Scheduled for {scheduleDate} at {scheduleTime}
                  </p>
                  <div className="modal-link-box">
                    <span className="modal-link-text">{scheduledLink}</span>
                    <button className="modal-copy-btn" onClick={handleCopyScheduledLink}>
                      <i className={`fas ${scheduledCopied ? "fa-check" : "fa-copy"}`}></i>
                      {scheduledCopied ? " Copied!" : " Copy"}
                    </button>
                  </div>
                  <button
                    className="modal-btn-secondary"
                    onClick={() => navigate(`/meet/${scheduledLink.split("/meet/")[1]}`)}
                  >
                    <i className="fas fa-arrow-right"></i> Open Now
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
